import {
  getMeOptions,
  postMeUpdateNameMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "@win-reality/react-ui";
import { backOff } from "exponential-backoff";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import smartparkLogo from "@/assets/smartpark-logo-black.png";
import googleLogo from "@/assets/social-google-logo.png";
import ResetPasswordFlowSupabase from "@/components/auth/ResetPasswordFlowSupabase";
import SignupFailureModal from "@/components/auth/SignupFailureModal";
import { supabaseClient } from "@/config/supabase";
import { useFacebookOAuth } from "@/hooks/useFacebookOAuth";
import { useGoogleOAuth } from "@/hooks/useGoogleOAuth";
import { validateRedirectPath } from "@/utils/validateRedirectPath";

import homepageBg from "../../assets/homepage-bg.jpg";

/**
 * Apple icon component
 * @deprecated - Temporarily unused, kept for future Apple OAuth implementation
 */
// @ts-expect-error - Intentionally unused, kept for future Apple OAuth implementation
const _AppleIcon = ({
  className,
  width = "19",
  height = "23",
}: {
  className?: string;
  width?: string | number;
  height?: string | number;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 19 23"
    fill="none"
    className={className}
  >
    <path
      d="M9.42261 6.59375C10.3186 6.59375 12.0237 5.35289 13.782 5.53809C14.6061 5.56402 15.4131 5.78229 16.1384 6.17578C16.8637 6.56928 17.4878 7.12697 17.9607 7.80469C17.2162 8.26403 16.5991 8.90521 16.1677 9.66797C15.7363 10.4307 15.5042 11.291 15.4929 12.168C15.494 13.1543 15.7845 14.1186 16.3279 14.9404C16.8714 15.7624 17.6442 16.4058 18.5496 16.79C18.1936 17.9504 17.657 19.0469 16.9597 20.0391C16.0233 21.4448 15.0415 22.8183 13.4832 22.8438C11.969 22.8788 11.46 21.9493 9.72339 21.9492C7.97032 21.9492 7.42766 22.8185 5.97534 22.8789C4.49128 22.9341 3.35695 21.3789 2.38647 19.9863C0.446707 17.1424 -1.06332 11.9714 0.96167 8.45312C1.42909 7.61036 2.10597 6.9036 2.92651 6.40137C3.74718 5.89911 4.68355 5.61811 5.64429 5.58691C7.12963 5.55625 8.5093 6.59356 9.42261 6.59375ZM13.7996 0C13.9009 1.32835 13.4823 2.64435 12.6326 3.66797C12.2257 4.1757 11.7098 4.58479 11.1228 4.86328C10.5358 5.1418 9.8932 5.28308 9.2439 5.27637C9.20248 4.63771 9.28656 3.99654 9.49194 3.39062C9.69739 2.78469 10.0202 2.22497 10.4412 1.74414C11.3023 0.759876 12.5015 0.136895 13.7996 0Z"
      fill="white"
    />
  </svg>
);

/**
 * Custom error for signup failures that includes an error code.
 * Allows us to distinguish between different failure types without using `any`.
 */
class SignupError extends Error {
  constructor(
    message: string,
    public code: string,
  ) {
    super(message);
    this.name = "SignupError";
  }
}

interface LoginPageProps {
  isSignup?: boolean;
}

const LoginPage = ({ isSignup = false }: LoginPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isSignupMode, setIsSignupMode] = useState(isSignup);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const {
    handleGoogleSignIn,
    handleGoogleSignUp,
    error: googleError,
    isLoading: googleIsLoading,
  } = useGoogleOAuth();
  const {
    handleFacebookSignIn: _handleFacebookSignIn,
    handleFacebookSignUp: _handleFacebookSignUp,
    error: facebookError,
    isLoading: facebookIsLoading,
  } = useFacebookOAuth();

  // Create mutation for updating user profile names
  // Suppress default error toast - errors are handled via SignupFailureModal
  const { mutateAsync: updateName } = useMutation({
    ...postMeUpdateNameMutation(),
    onError: (error: Error) => {
      console.error("[LoginPage] /update-name error:", error);
      // Don't show toast - errors are shown via SignupFailureModal
    },
  });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Combine OAuth errors from both providers
  const oauthError = googleError || facebookError;
  const oauthIsLoading = googleIsLoading || facebookIsLoading;

  // Signup failure tracking
  const [signupFailureState, setSignupFailureState] = useState<{
    isOpen: boolean;
    failedCalls: ("supabase" | "me" | "updateName")[];
  }>({
    isOpen: false,
    failedCalls: [],
  });

  /**
   * Redirect user after successful authentication.
   *
   * Logic:
   * 1. If explicit-logout flag is set: redirect to home (user explicitly logged out)
   * 2. If "from" location is valid: redirect there (user was interrupted mid-flow)
   * 3. Otherwise: redirect to / where RoleBasedHomeRedirect will route based on loaded roles
   *
   * Note: We don't compute the home route here because user data hasn't loaded yet.
   * RoleBasedHomeRedirect on "/" waits for all data to load before deciding where to route.
   */
  const handlePostAuthRedirect = () => {
    // Check for explicit logout flag
    const wasExplicitLogout =
      localStorage.getItem("explicit-logout") === "true";
    if (wasExplicitLogout) {
      localStorage.removeItem("explicit-logout");
      navigate("/", { replace: true });
      return;
    }

    // Check for validated "from" location (where user was redirected from)
    const { from } = (location.state as { from?: string } | null) || {};
    const validatedFrom = validateRedirectPath(from);

    if (validatedFrom) {
      navigate(validatedFrom, { replace: true });
      return;
    }

    // Default: redirect to home - RoleBasedHomeRedirect will route from there
    navigate("/", { replace: true });
  };

  // Guard: Redirect logged-in users away from login page
  useEffect(() => {
    const checkSession = async () => {
      // Check Supabase session
      const { data } = await supabaseClient.auth.getSession();
      const hasSupabaseSession = !!data?.session;

      // Redirect if authenticated
      if (hasSupabaseSession) {
        navigate("/", { replace: true });
      }
    };
    checkSession();
  }, [navigate]);

  // Validation logic
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPasswordValid = password.length >= 8;
  const isFirstNameValid = !isSignupMode || firstName.trim().length > 0;
  const isLastNameValid = !isSignupMode || lastName.trim().length > 0;
  const isFormValid =
    isEmailValid && isPasswordValid && isFirstNameValid && isLastNameValid;

  const handleEmailAuth = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setEmailError(null);
    setSignupFailureState({ isOpen: false, failedCalls: [] });

    try {
      if (isSignupMode) {
        // For signup, we need to run 3 sequential calls with exponential backoff:
        // 1. Supabase signup
        // 2. Fetch user from /me
        // 3. Save names via /me/update-name
        // All are blocking; if any fail, show modal for retry

        await performSignup([]);
      } else {
        // Sign-in flow
        const { error: signInError } =
          await supabaseClient.auth.signInWithPassword({
            email,
            password,
          });

        if (signInError) {
          // Don't log to console - this error is expected during password attempt
          // before OAuth fallback. It will be shown in the UI instead.
          setError(signInError.message);
          setIsLoading(false);
          return;
        }

        // Invalidate the /me query cache so it fetches fresh data with new auth
        queryClient.invalidateQueries(getMeOptions());
        handlePostAuthRedirect();
      }
    } catch (err) {
      console.error("[LoginPage] Unexpected error:", err);
      setError("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const performSignup = async (
    failedCalls: ("supabase" | "me" | "updateName")[],
  ) => {
    const callsToRun =
      failedCalls.length > 0
        ? failedCalls
        : (["supabase", "me", "updateName"] as const);
    const failedCallsList: ("supabase" | "me" | "updateName")[] = [];

    try {
      // Step 1: Supabase signup (with exponential backoff if retrying)
      if (callsToRun.includes("supabase")) {
        try {
          await backOff(
            async () => {
              const { error: signUpError } = await supabaseClient.auth.signUp({
                email,
                password,
              });

              if (signUpError) {
                // Check for user_already_exists error
                if (
                  signUpError.message.includes("user_already_exists") ||
                  signUpError.message.includes("User already registered")
                ) {
                  throw new SignupError(
                    "user_already_exists",
                    "user_already_exists",
                  );
                }
                throw new Error(signUpError.message);
              }
            },
            {
              startingDelay: 1000,
              timeMultiple: 2,
              numOfAttempts: 3,
              maxDelay: 10000,
            },
          );
          // Invalidate the /me query cache so it fetches fresh data with new auth
          queryClient.invalidateQueries(getMeOptions());
        } catch (err) {
          console.error("[LoginPage] Supabase signup failed:", err);

          // Handle user_already_exists error with inline email field error
          if (
            err instanceof SignupError &&
            err.code === "user_already_exists"
          ) {
            setEmailError("An account with this email already exists");
            setIsLoading(false);
            return;
          }

          failedCallsList.push("supabase");
          throw err; // Exit to show modal
        }
      }

      // Step 2: Fetch user from /me (with exponential backoff if retrying)
      if (callsToRun.includes("me")) {
        try {
          await backOff(
            async () => {
              const meResult = await queryClient.fetchQuery(getMeOptions());
              if (!meResult?.userId) {
                throw new Error("Failed to fetch user profile");
              }
            },
            {
              startingDelay: 1000,
              timeMultiple: 2,
              numOfAttempts: 3,
              maxDelay: 10000,
            },
          );
        } catch (err) {
          console.error("[LoginPage] /me failed:", err);
          failedCallsList.push("me");
          throw err; // Exit to show modal
        }
      }

      // Step 3: Save names via /me/update-name (with exponential backoff if retrying)
      if (callsToRun.includes("updateName")) {
        try {
          await backOff(
            async () => {
              await updateName({
                body: {
                  givenName: firstName,
                  familyName: lastName,
                },
              });
            },
            {
              startingDelay: 1000,
              timeMultiple: 2,
              numOfAttempts: 3,
              maxDelay: 10000,
            },
          );
        } catch (err) {
          console.error("[LoginPage] /update-name failed:", err);
          failedCallsList.push("updateName");
          throw err; // Exit to show modal
        }
      }

      // All calls succeeded
      handlePostAuthRedirect();
    } catch (err) {
      console.error("[LoginPage] Signup failed, showing retry modal");

      setSignupFailureState({
        isOpen: true,
        failedCalls: failedCallsList,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // @ts-expect-error - Intentionally unused, kept for future Apple OAuth implementation
  const _handleAppleOAuth = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { error: oauthError } = await supabaseClient.auth.signInWithOAuth({
        provider: "apple",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (oauthError) {
        console.error("[OAuth] Apple error:", oauthError.message);
        setError(oauthError.message);
      }
    } catch (err) {
      console.error("[OAuth] Apple exception:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-cover bg-center bg-no-repeat flex items-center justify-center py-8"
      style={{ backgroundImage: `url(${homepageBg})` }}
    >
      <div className="bg-white rounded-2xl shadow-xl px-12 py-14 flex flex-col items-center max-w-md w-full mx-4 max-h-[calc(100vh-64px)] overflow-y-auto">
        <img
          src={smartparkLogo}
          className="mb-8 max-h-16 w-auto max-w-full"
          alt="SmartPark logo"
        />
        {isForgotPasswordMode && (
          <h2 className="text-xl font-semibold text-gray-900 mb-8">
            Reset Password
          </h2>
        )}

        {(error || oauthError) && (
          <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error || oauthError}
          </div>
        )}

        {/* Forgot Password Form - OTP Flow */}
        {isForgotPasswordMode && <ResetPasswordFlowSupabase />}

        {/* Email/Password Form */}
        {!isForgotPasswordMode && (
          <form onSubmit={handleEmailAuth} className="w-full space-y-4 mb-2">
            {/* First/Last Name Fields - Only for Signup */}
            {isSignupMode && (
              <>
                <div>
                  <label htmlFor="firstName" className="block text-sm mb-2">
                    First Name
                  </label>
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                    required={isSignupMode}
                    isDisabled={isLoading}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm mb-2">
                    Last Name
                  </label>
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                    required={isSignupMode}
                    isDisabled={isLoading}
                  />
                </div>
              </>
            )}

            <div>
              <label htmlFor="email" className="block text-sm mb-2">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(null); // Clear error when user starts typing
                }}
                placeholder="Email"
                required
                isDisabled={isLoading}
              />
              {emailError && (
                <p className="text-xs text-red-600 mt-1">{emailError}</p>
              )}
            </div>
            <div>
              <label htmlFor="password" className="block text-sm mb-2">
                Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                isDisabled={isLoading}
              />
              {isSignupMode && (
                <p className="text-xs text-black mt-2">
                  Password must contain at least 8 characters
                </p>
              )}
              {!isSignupMode && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPasswordMode(true);
                      setError(null);
                      setEmail("");
                      setPassword("");
                    }}
                    className="text-sm text-black font-semibold hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            <Button
              type="submit"
              variant="secondary-flat-light"
              isDisabled={isLoading || !isFormValid}
              isLoading={isLoading && isSignupMode}
              className="w-full"
            >
              {isSignupMode ? "Create Account" : "Sign In"}
            </Button>
          </form>
        )}

        {/* Toggle Links */}
        {isForgotPasswordMode ? (
          <button
            type="button"
            onClick={() => {
              setIsForgotPasswordMode(false);
              setError(null);
              setEmail("");
              setPassword("");
            }}
            className="text-sm text-black font-semibold hover:underline mt-6 mb-6"
          >
            Back to Sign In
          </button>
        ) : (
          <p className="text-sm text-black mt-6 mb-6">
            {isSignupMode ? (
              <>
                Have a Win Reality account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setIsSignupMode(false);
                    setError(null);
                    setFirstName("");
                    setLastName("");
                    setEmail("");
                    setPassword("");
                  }}
                  className="text-black font-semibold hover:underline"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setIsSignupMode(true);
                    setError(null);
                    setFirstName("");
                    setLastName("");
                    setEmail("");
                    setPassword("");
                  }}
                  className="text-black font-semibold hover:underline"
                >
                  Sign up
                </button>
              </>
            )}
          </p>
        )}

        {/* OAuth Section - Hidden in Forgot Password Mode */}
        {!isForgotPasswordMode && (
          <>
            {/* Divider */}
            <div className="w-full flex items-center gap-3 mb-6">
              <div className="flex-1 border-t border-gray-300" />
              <span className="text-sm text-gray-500">or</span>
              <div className="flex-1 border-t border-gray-300" />
            </div>

            {/* OAuth Buttons */}
            {/* Mobile: Inline Icon Buttons */}
            <div className="md:hidden w-full flex items-center justify-center gap-3">
              {/* Google Mobile Button */}
              <Button
                type="button"
                variant="secondary-flat-light"
                size="icon-lg"
                onClick={() =>
                  isSignupMode ? handleGoogleSignUp() : handleGoogleSignIn()
                }
                isDisabled={isLoading || oauthIsLoading}
                isLoading={googleIsLoading}
                className="flex items-center justify-center"
              >
                <img
                  src={googleLogo}
                  alt="Google logo"
                  className="w-7 max-w-none"
                />
              </Button>

              {/* Facebook Mobile Button - TEMPORARILY REMOVED */}
              {/* <Button
                type="button"
                variant="secondary-flat-light"
                size="icon-lg"
                onClick={() =>
                  isSignupMode ? handleFacebookSignUp() : handleFacebookSignIn()
                }
                isDisabled={isLoading || oauthIsLoading}
                isLoading={facebookIsLoading}
                className="flex items-center justify-center"
              >
                <img
                  src={facebookLogo}
                  alt="Facebook logo"
                  className="w-7 max-w-none"
                />
              </Button> */}

              {/* Apple Mobile Button - TEMPORARILY REMOVED */}
              {/* <Button
                type="button"
                variant="secondary-flat-light"
                size="icon-lg"
                onClick={handleAppleOAuth}
                isDisabled={isLoading}
                className="flex items-center justify-center"
              >
                <AppleIcon className="w-7 h-7" width="28" height="28" />
              </Button> */}
            </div>

            {/* Desktop: Full-Width Text Buttons */}
            <div className="hidden md:block w-full space-y-3">
              {/* Google Desktop Button */}
              <Button
                type="button"
                variant="secondary-flat-light"
                onClick={() =>
                  isSignupMode ? handleGoogleSignUp() : handleGoogleSignIn()
                }
                isDisabled={isLoading || oauthIsLoading}
                isLoading={googleIsLoading}
                className="w-full flex items-center justify-center gap-3"
              >
                <img src={googleLogo} alt="Google logo" className="w-5" />
                {isSignupMode ? "Sign up with Google" : "Continue with Google"}
              </Button>

              {/* Facebook Desktop Button - TEMPORARILY REMOVED */}
              {/* <Button
                type="button"
                variant="secondary-flat-light"
                onClick={() =>
                  isSignupMode ? handleFacebookSignUp() : handleFacebookSignIn()
                }
                isDisabled={isLoading || oauthIsLoading}
                isLoading={facebookIsLoading}
                className="w-full flex items-center justify-center gap-3"
              >
                <img src={facebookLogo} alt="Facebook logo" className="w-5" />
                {isSignupMode
                  ? "Sign up with Facebook"
                  : "Continue with Facebook"}
              </Button> */}

              {/* Apple Desktop Button - TEMPORARILY REMOVED */}
              {/* <Button
                type="button"
                variant="secondary-flat-light"
                onClick={handleAppleOAuth}
                isDisabled={isLoading}
                className="w-full flex items-center justify-center gap-3"
              >
                <AppleIcon />
                {isSignupMode ? "Sign up with Apple" : "Continue with Apple"}
              </Button> */}
            </div>
          </>
        )}
      </div>

      {/* Signup Failure Modal */}
      <SignupFailureModal
        isOpen={signupFailureState.isOpen}
        isLoading={isLoading}
        onRetry={() => {
          setIsLoading(true);
          performSignup(signupFailureState.failedCalls);
        }}
        onClose={() => {
          setSignupFailureState({ isOpen: false, failedCalls: [] });
        }}
      />
    </div>
  );
};

export default LoginPage;
